import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import GetCookie from '../../utils/GetCookie';
import ProcessingPopup from '../../utils/popup/ProcessingPopup';
import ErrorPopup from '../../utils/popup/ErrorPopup';

const STEP_TYPE_BADGE = {
  data_retrieval: { label: 'Data Retrieval', cls: 'ev-message-card2' },
  analysis:       { label: 'Analysis',       cls: 'ev-message-card2' },
  final_response: { label: 'Final Response', cls: 'ev-message-card3-green' },
};

const isCodeString = s => {
  const t = s.trim();
  return (
    t.startsWith('{') || t.startsWith('[') ||
    t.startsWith('def ') || t.startsWith('import ') || t.startsWith('from ') ||
    t.startsWith('library(') || t.startsWith('#') ||
    t.startsWith('SELECT') || t.startsWith('select') ||
    /^\s*\w+\s*=/.test(t)
  );
};

const CopyButton = ({ text }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <button
      type="button"
      className="ev-btn-light-fill ev-btn-xsmall ev-radius-xsmall"
      onClick={handleCopy}
    >
      <i className={`bi ${copied ? 'bi-check2' : 'bi-copy'} me-1`}></i>
      {copied ? 'Copied' : 'Copy'}
    </button>
  );
};

const ChunkContent = ({ content }) => {
  const [expanded, setExpanded] = useState(false);
  return (
    <div>
      <p className="ev-xsmall-p-dark mb-1" style={{ lineHeight: 1.5, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
        {expanded ? content : content?.slice(0, 300)}
        {!expanded && content?.length > 300 ? '…' : ''}
      </p>
      {content?.length > 300 && (
        <button
          type="button"
          className="ev-btn-text-only-fill ev-btn-xsmall p-0"
          onClick={e => { e.stopPropagation(); setExpanded(p => !p); }}
        >
          {expanded ? 'Show less' : 'Show more'}
        </button>
      )}
    </div>
  );
};

const DocImagesList = ({ images }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="mt-1">
      <button
        type="button"
        className="ev-btn-text-only-fill ev-btn-xsmall p-0 d-flex align-items-center gap-1"
        onClick={e => { e.stopPropagation(); setOpen(p => !p); }}
      >
        <i className={`bi bi-chevron-${open ? 'up' : 'down'}`}></i>
        <i className="bi bi-images me-1"></i>
        <span>{images.length} image{images.length > 1 ? 's' : ''} retrieved</span>
      </button>
      {open && (
        <div className="d-flex flex-column gap-1 mt-1">
          {images.map((img, i) => (
            <div
              key={i}
              className="ev-card-8 p-2 ev-radius-xsmall d-flex align-items-start gap-2"
              style={{ borderLeft: '3px solid var(--color1)' }}
            >
              <i className="bi bi-file-image ev-small-p-blue mt-1"></i>
              <div className="mw-0 flex-grow-1">
                <p className="ev-xsmall-p-dark mb-0 text-truncate" title={img.s3_path}>
                  {img.s3_path?.split('/').pop() || `Image ${i + 1}`}
                </p>
                {img.caption && (
                  <p className="ev-xsmall-p-dark mb-0 mt-1" style={{ opacity: 0.6 }}>
                    {img.caption}
                  </p>
                )}
                {img.page != null && (
                  <span className="ev-xsmall-p-dark" style={{ opacity: 0.5 }}>
                    Page {img.page}
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const CHUNK_LIMIT = 5;

const ToolCard = ({ tool }) => {
  const [open, setOpen] = useState(false);
  const [chunksOpen, setChunksOpen] = useState(false);
  const [showAllChunks, setShowAllChunks] = useState({});

  const hasContent =
    tool.generated_code ||
    tool.result_summary ||
    tool.error ||
    tool.inputs ||
    tool.retrieved_chunks?.length ||
    tool.retrieved_images?.length;

  return (
    <div className="ev-card-8 mb-2 p-2 ev-radius-small">
      <div
        className="d-flex align-items-center justify-content-between"
        style={{ cursor: hasContent ? 'pointer' : 'default' }}
        onClick={() => hasContent && setOpen(p => !p)}
      >
        <div className="d-flex align-items-center gap-2">
          <span className="ev-bold-med ev-small-p-dark">{tool.tool}</span>
        </div>
        <div className="d-flex align-items-center gap-2">
          {hasContent && <i className={`bi bi-chevron-${open ? 'up' : 'down'} ev-small-p-blue`}></i>}
        </div>
      </div>

      {open && (
        <div className="mt-2 pt-2 border-top">
          {tool.inputs && (
            <div className="mb-2">
              {tool.inputs.query && (
                <div className="ev-message-card1 ev-btn-xsmall d-inline-block mb-1 ev-radius-xsmall">
                  Query: {tool.inputs.query}
                </div>
              )}
              {tool.inputs.doc_ids?.length > 0 && (
                <div className="ev-small-p-dark mb-1">
                  Docs: {tool.inputs.doc_ids.join(', ')}
                </div>
              )}
              {tool.inputs.doc_sources?.length > 0 && (
                <div className="ev-small-p-dark mb-1">
                  Sources: {tool.inputs.doc_sources.map(d => `doc_${d.doc_id}`).join(', ')}
                </div>
              )}
              {tool.inputs.image_count > 0 && (
                <div className="ev-small-p-dark mb-1">
                  <i className="bi bi-images me-1"></i>{tool.inputs.image_count} image(s) analysed
                </div>
              )}
            </div>
          )}

          {tool.error && (
            <div className="ev-alert-card-warning mb-2 ev-radius-xsmall">
              <b className="bi bi-exclamation-triangle me-1"> Error:</b>{tool.error}
            </div>
          )}

          {tool.generated_code && (
            <div className="mb-2">
              <div className="ev-bold-med ev-xsmall-p-dark mb-1">
                <i className="bi bi-code-slash me-1"></i>Generated Code
              </div>
              <pre className="ev-card-2 ev-radius-xsmall ev-small-p-dark scrollable-element vh-30 position-relative">
                <div className="position-absolute top-0 end-0 p-1">
                  <CopyButton text={tool.generated_code} />
                </div>
                {tool.generated_code}
              </pre>
            </div>
          )}

          {tool.result_summary && (() => {
            const isCode = isCodeString(tool.result_summary);
            return (
              <div className="mb-2">
                <div className="ev-bold-med ev-xsmall-p-dark mb-1 opacity-75">
                  <i className={`bi ${isCode ? 'bi-code-slash' : 'bi-bar-chart'} me-1`}></i>Result Summary
                </div>
                <pre className="ev-card-2 ev-small-p-dark ev-radius-xsmall scrollable-element vh-30 position-relative">
                  <div className="position-absolute top-0 end-0 p-1">
                    <CopyButton text={tool.result_summary} />
                  </div>
                  {tool.result_summary}
                </pre>
              </div>
            );
          })()}

          {/* retrieved chunks */}
          {tool.retrieved_chunks?.length > 0 && (
            <div className="mt-2">
              <button
                type="button"
                className="ev-btn-light-fill ev-btn-xsmall d-flex align-items-center gap-1 mb-2 ev-radius-xsmall"
                onClick={e => { e.stopPropagation(); setChunksOpen(p => !p); }}
              >
                <i className={`bi bi-chevron-${chunksOpen ? 'up' : 'down'}`}></i>
                <i className="bi bi-file-earmark-text me-1"></i>
                Retrieved Chunks ({tool.retrieved_chunks.length} doc{tool.retrieved_chunks.length > 1 ? 's' : ''})
              </button>

              {chunksOpen && (
                <div className="d-flex flex-column gap-2">
                  {tool.retrieved_chunks.map((docResult, di) => {
                    const allChunks = docResult.chunks || [];
                    const isExpanded = showAllChunks[di];
                    const visibleChunks = isExpanded ? allChunks : allChunks.slice(0, CHUNK_LIMIT);
                    const remaining = allChunks.length - CHUNK_LIMIT;

                    return (
                      <div key={di} className="ev-card-2 ev-radius-xsmall p-2">
                        {/* doc header */}
                        <div className="d-flex align-items-center gap-2 mb-2 flex-wrap">
                          <span className="ev-message-card1 ev-btn-xsmall ev-radius-xsmall">
                            doc_{docResult.doc_id}
                          </span>
                          {docResult['document title'] && (
                            <span className="ev-xsmall-p-dark ev-bold-med" style={{ opacity: 0.85 }}>
                              {docResult['document title']}
                            </span>
                          )}
                        </div>

                        {/* chunks with limit */}
                        {visibleChunks.map((chunk, ci) => (
                          <div
                            key={ci}
                            className="ev-card-8 mb-1 p-2 ev-radius-xsmall"
                            style={{ borderLeft: '3px solid var(--color1)' }}
                          >
                            <div className="d-flex justify-content-between align-items-center mb-1">
                              <span className="ev-xsmall-p-dark" style={{ opacity: 0.6 }}>
                                Chunk {ci + 1}
                              </span>
                              <span className="ev-message-card3 ev-btn-xsmall">
                                {(chunk.cosine_similarity * 100).toFixed(1)}% match
                              </span>
                            </div>
                            <ChunkContent content={chunk.current_chunk?.chunk_content} />
                          </div>
                        ))}

                        {/* show more / show less toggle */}
                        {allChunks.length > CHUNK_LIMIT && (
                          <button
                            type="button"
                            className="ev-btn-text-only-fill ev-btn-xsmall p-0 mt-1 d-flex align-items-center gap-1"
                            onClick={e => {
                              e.stopPropagation();
                              setShowAllChunks(prev => ({ ...prev, [di]: !prev[di] }));
                            }}
                          >
                            <i className={`bi bi-chevron-${isExpanded ? 'up' : 'down'}`}></i>
                            {isExpanded ? 'Show less' : `Show more (${remaining}+)`}
                          </button>
                        )}

                        {/* images per doc */}
                        {docResult.images_data?.length > 0 && (
                          <DocImagesList images={docResult.images_data} />
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* retrieved images — only from image_analysis tool */}
          {tool.retrieved_images?.length > 0 && (
            <div className="mt-2">
              <div className="ev-bold-med ev-xsmall-p-dark mb-1 opacity-75">
                <i className="bi bi-images me-1"></i>Analysed Images ({tool.retrieved_images.length})
              </div>
              <div className="d-flex flex-column gap-1">
                {tool.retrieved_images.map((img, i) => (
                  <div
                    key={i}
                    className="ev-card-8 p-2 ev-radius-xsmall d-flex align-items-start gap-2"
                    style={{ borderLeft: '3px solid var(--color1)' }}
                  >
                    <i className="bi bi-file-image ev-small-p-blue mt-1"></i>
                    <div className="mw-0 flex-grow-1">
                      <p className="ev-xsmall-p-dark mb-0 text-truncate" title={img.s3_path}>
                        {img.s3_path?.split('/').pop() || `Image ${i + 1}`}
                      </p>
                      {img.caption && (
                        <p className="ev-xsmall-p-dark mb-0 mt-1" style={{ opacity: 0.6 }}>
                          {img.caption}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const ChainAccordion = ({ step, index }) => {
  const [open, setOpen] = useState(false);
  const badge = STEP_TYPE_BADGE[step.type] || STEP_TYPE_BADGE.analysis;
  const toolCount = step.tool_executions?.length || 0;

  return (
    <div className="mb-2">
      <button
        type="button"
        className="w-100 d-flex align-items-center justify-content-between ev-message-card2 border-0 text-start"
        style={{ borderRadius: open ? 'var(--px-10) var(--px-10) 0 0' : 'var(--px-10)', padding: 'var(--px-10) var(--px-15)' }}
        onClick={() => setOpen(p => !p)}
      >
        <div className="d-flex align-items-center gap-2 mw-0">
          <span className="ev-bold-med ev-xsmall-p-dark text-nowrap">Chain {index + 1}</span>
          <span className={`${badge.cls} ev-btn-xsmall ev-radius-xlarge text-nowrap`}>{badge.label}</span>
          {toolCount > 0 && (
            <span className="ev-xsmall-p-dark text-nowrap">
              {toolCount} tool{toolCount > 1 ? 's' : ''}
            </span>
          )}
        </div>
        <i className={`bi bi-chevron-${open ? 'up' : 'down'} ev-small-p-blue ms-2`}></i>
      </button>

      {open && (
        <div className="ev-card-2 ev-padding-small ev-radius-xsmall">
          {step.description && (
            <div className="mb-3">
              <div className="ev-bold-med ev-small-p-blue mb-1">
                <i className="bi bi-card-text me-1"></i>Description
              </div>
              <p className="ev-small-p-dark mb-0 lh-lg">{step.description}</p>
            </div>
          )}

          {step.thinking && step.type !== 'final_response' && (
            <div className="mb-3">
              <div className="ev-bold-med ev-small-p-blue mb-1">
                <i className="bi bi-lightbulb me-1"></i>Reasoning
              </div>
              <p className="ev-small-p-dark mb-0">{step.thinking}</p>
            </div>
          )}

          {toolCount > 0 && (
            <div>
              <div className="ev-bold-med ev-small-p-blue mb-2">
                Tool Executions
              </div>
              {step.tool_executions.map((t, i) => <ToolCard key={i} tool={t} />)}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const AgentLogModal = ({ agentId, fabricId, version, agentName, onClose, retrievalConfidence }) => {
  const [logs, setLogs] = useState(null);
  const [isProcessingPopupVisible, setIsProcessingPopupVisible] = useState(false);
  const [processingMessage, setProcessingMessage] = useState('');
  const [isErrorPopupVisible, setIsErrorPopupVisible] = useState(false);
  const [popupErrorMessage, setPopupErrorMessage] = useState('');

  const showError = (msg) => { setPopupErrorMessage(msg); setIsErrorPopupVisible(true); };
  const closeErrorPopup = () => { setIsErrorPopupVisible(false); setPopupErrorMessage(''); };

  const rc =
    retrievalConfidence && Object.keys(retrievalConfidence).length > 0
      ? retrievalConfidence
      : null;

  useEffect(() => {
    const fetchLogs = async () => {
      try {
        setProcessingMessage('Loading execution logs...');
        setIsProcessingPopupVisible(true);
        const res = await fetch('/pceapi/genbi/get-agent-logs', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: GetCookie('access_token') },
          body: JSON.stringify({ agent_id: agentId, fabric_id: fabricId, version }),
        });
        const json = await res.json();
        if (json.status === 'success') {
          setLogs(json.data);
        } else {
          showError(json.message || 'Failed to fetch logs');
        }
      } catch (e) {
        showError('Network error: ' + e.message);
      } finally {
        setIsProcessingPopupVisible(false);
      }
    };
    fetchLogs();
  }, [agentId, fabricId, version]);

  return createPortal(
    <>
      {isProcessingPopupVisible && <ProcessingPopup message={processingMessage} />}
      {isErrorPopupVisible && <ErrorPopup message={popupErrorMessage} onClose={closeErrorPopup} />}

      <div
        className="ev-modal-overlay d-flex align-items-center justify-content-center p-3"
        style={{ zIndex: "var(--num-1000000000)" }}
        onClick={e => { if (e.target === e.currentTarget) onClose(); }}
      >
        <div className="ev-card-4 d-flex flex-column overflow-hidden vh-80 w-60">
          {/* header */}
          <div className="d-flex align-items-center justify-content-between ev-padding-small border-bottom flex-shrink-0">
            <div>
              <div className="d-flex align-items-center gap-2 mb-1">
                <i className="bi bi-journal-text ev-small-p-blue"></i>
                <span className="ev-bold-med ev-reg-p-dark">Execution Logs</span>
              </div>
              {agentName && <span className="ev-message-card1 ev-btn-xsmall">{agentName}</span>}
            </div>
            <button type="button" className="ev-btn-light-fill ev-btn-xsmall ev-radius-xlarge" onClick={onClose}>
              <i className="bi bi-x-lg"></i>
            </button>
          </div>

          {/* body */}
          <div className="ev-padding-small scrollable-element flex-grow-1 pt-2">
            {rc && (
              <div className="ev-card-2 ev-radius-small ev-padding-small mb-3">
                <div className="ev-bold-med ev-small-p-blue mb-2">
                  <i className="bi bi-bullseye me-1"></i>Retrieval Confidence
                </div>
                {rc.chunk_count > 0 ? (
                  <div className="d-flex flex-wrap align-items-center gap-2">
                    <span className="ev-message-card3 ev-btn-xsmall">
                      Mean {(rc.mean_cosine_similarity * 100).toFixed(1)}%
                    </span>
                    <span className="ev-message-card3 ev-btn-xsmall">
                      Min {(rc.min_cosine_similarity * 100).toFixed(1)}%
                    </span>
                    <span className="ev-xsmall-p-dark">
                      {rc.chunk_count} chunk{rc.chunk_count !== 1 ? 's' : ''}
                    </span>
                    <span className="ev-xsmall-p-dark" style={{ opacity: 0.4 }}>·</span>
                    <span className="ev-xsmall-p-dark">
                      {rc.search_iterations} search iteration{rc.search_iterations !== 1 ? 's' : ''}
                    </span>
                  </div>
                ) : (
                  <p className="ev-xsmall-p-dark mb-0" style={{ opacity: 0.6 }}>
                    No chunks retrieved for this run.
                  </p>
                )}
              </div>
            )}
            {!logs && !isProcessingPopupVisible && !isErrorPopupVisible && (
              <div className="ev-card-2 text-center ev-padding-reg ev-radius-small">
                <i className="bi bi-clock-history ev-fs-25 ev-small-p-blue d-block mb-2"></i>
                <p className="ev-small-p-dark mb-0">
                  Logs not available for this version.<br />
                </p>
              </div>
            )}

            {logs && (
              <>
                <div className="d-flex align-items-center gap-2 mb-3">
                  <span className="ev-message-card3 ev-btn-xsmall">
                    <i className="bi bi-link-45deg me-1"></i>
                    {logs.steps.filter(s => s.type !== 'final_response').length} chain{logs.steps.filter(s => s.type !== 'final_response').length !== 1 ? 's' : ''}
                  </span>
                </div>
                {logs.steps
                  .filter(s => s.type !== 'final_response')
                  .map((step, i) => (
                    <ChainAccordion key={step.step} step={step} index={i} />
                  ))}
              </>
            )}
          </div>
        </div>
      </div>
    </>,
    document.body
  );
};

export default AgentLogModal;